import React from 'react'
import { Box } from '@node-space/ui-components'
import type {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form'
import type { BankDetailsFormReturn } from 'pages/Contacts/hooks/useBankDetailsForm'
import type { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import type { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import { AccountNumberField } from './AccountNumberField'
import { BankCodeField } from './BankCodeField'
import { InternationalBankNameAddressDetails } from './InternationalBankNameAddressCountry'
import { IntermediaryBankDetailsToggle } from './IntermediaryBankDetailsToggle'
import { SwiftCodeField } from './SwiftCodeField'
import { BeneficiaryDetailsCurrency } from 'types/beneficiaries'

type EurGbpBankDetailsProps = {
  variant: 'EUR' | 'GBP'
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  bankDetailsForm: BankDetailsFormReturn
}

export const EurGbpBankDetails = ({
  variant,
  formFieldLabels,
  formValues,
  errors,
  clearErrors,
  register,
  setError,
  setValue,
  bankDetailsForm,
}: EurGbpBankDetailsProps) => {
  const {
    isInternational,
    isLocal,
    isLocalOnly,
    isAccountNumberAndBankCodeInline,
    isValidAccountNumber,
    isValidBankCode,
    isBankDetailsValidationLoading,
    isValidatingRoutingNumber,
    isValidatingScan,
    isValidatingBic,
    countriesLoading,
    internationalCountryList,
    onFieldUpdate,
    handleAccountNumberOnBlur,
    handleBankCodeOnBlur,
    onInternationalCountrySelection,
  } = bankDetailsForm

  const { accountNumber, bankCode } = formValues || {}

  const showMainDetails = variant === BeneficiaryDetailsCurrency.GBP ? isLocal || isInternational : true
  const showSwiftCode = isInternational
  const showBankCode = variant === BeneficiaryDetailsCurrency.GBP ? (isInternational && isLocal) || isLocalOnly : isLocalOnly

  const swiftPlacement: 'inline' | 'separate' = variant === BeneficiaryDetailsCurrency.EUR ? 'inline' : 'separate'

  return (
    <Box flexCol width="full" gapY={16} paddingT={12}>
      {showMainDetails && (
        <Box
          gapX={12}
          gapY={16}
          width="full"
          flex={isAccountNumberAndBankCodeInline}
          flexCol={!isAccountNumberAndBankCodeInline}
        >
          <Box
            {...(isAccountNumberAndBankCodeInline && {
              flexSize: 'auto',
              width: 'full',
              gapX: 12,
            })}
          >
            <AccountNumberField
              register={register}
              errors={errors}
              formFieldLabels={formFieldLabels}
              isBankDetailsValidationLoading={isBankDetailsValidationLoading}
              isValidAccountNumber={isValidAccountNumber}
              accountNumber={accountNumber}
              onBlur={handleAccountNumberOnBlur}
              onChange={onFieldUpdate('accountNumber')}
            />
          </Box>

          {showBankCode && (
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <BankCodeField
                register={register}
                formValues={formValues}
                errors={errors}
                formFieldLabels={formFieldLabels}
                isLoading={isValidatingRoutingNumber || isValidatingScan || isValidatingBic}
                isValidBankCode={isValidBankCode}
                bankCode={bankCode}
                onChange={onFieldUpdate('bankCode')}
                onBlur={handleBankCodeOnBlur}
              />
            </Box>
          )}

          {swiftPlacement === 'inline' && showSwiftCode && (
            <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
              <SwiftCodeField
                formValues={formValues}
                register={register}
                errors={errors}
                onChange={onFieldUpdate('swiftCode')}
              />
            </Box>
          )}
        </Box>
      )}

      {swiftPlacement === 'separate' && showSwiftCode && (
        <Box {...(isAccountNumberAndBankCodeInline && { flexSize: 'auto', width: 'full' })}>
          <SwiftCodeField formValues={formValues} register={register} errors={errors} onChange={onFieldUpdate('swiftCode')} />
        </Box>
      )}

      {isInternational && (
        <InternationalBankNameAddressDetails
          formValues={formValues}
          errors={errors}
          clearErrors={clearErrors}
          register={register}
          setError={setError}
          setValue={setValue}
          onFieldUpdate={onFieldUpdate}
          countriesLoading={countriesLoading}
          internationalCountryList={internationalCountryList}
          onInternationalCountrySelection={onInternationalCountrySelection}
        />
      )}

      {isInternational && (
        <IntermediaryBankDetailsToggle
          register={register}
          checked={formValues?.isIntermediaryBankDetailsRequired}
          onCheckedChange={checked => setValue('isIntermediaryBankDetailsRequired', checked)}
        />
      )}
    </Box>
  )
}

